import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { BrandingStatus, BrandingType } from 'types/api/api.types';
import { isLocalOrQaEnvironment } from 'core/utils/environment';
import { resetSeasonalIntroPlayed } from 'shared/hooks/useSeasonalPalette';

const BRANDING_STORAGE_KEY = 'lobby_seasonalBranding';

export type Flags = {
    seasonalBranding: BrandingType | null;
    enableLobbyDeposit: boolean;
    enablePrizePopCampaign: boolean;
    enableTournamentsCampaign: boolean;
    enableRebateCampaign: boolean;
};

type FeatureFlagsState = {
    flags: Flags;
    seasonalBrandingThumbnailsConstants: Record<string, string[]>;
    seasonalBrandingBadgesConstants: Record<string, string[]>;
    seasonalBrandingEnabled: boolean;
    brandingLoadStatus: Partial<Record<BrandingType, BrandingStatus>>;
    setSeasonalBranding: (seasonalBranding: BrandingType) => void;
    setSeasonalBrandingEnabled: (enabled: boolean) => void;
    setBrandingLoadStatus: (name: BrandingType, status: BrandingStatus) => void;
    setFlags: (flags: Flags) => void;
    resetFeatureFlags: () => void;
};

const initialFlags: Flags = { seasonalBranding: null, enableLobbyDeposit: false, enablePrizePopCampaign: true, enableTournamentsCampaign: true, enableRebateCampaign: isLocalOrQaEnvironment() };

// The array values are game keys, that should use seasonal branding thumbnails
const initialSeasonalBrandingThumbnailsConstants: Record<string, string[]> = {
    christmas: ['InstMrAction', 'InstRico']
};

// The array values are jackpot names that should use seasonal branding badges
const initialSeasonalBrandingBadgesConstants: Record<string, string[]> = {
    christmas: ['bell link', 'vip bell link']
};

const initialSeasonalBrandingEnabled = localStorage.getItem(BRANDING_STORAGE_KEY) !== 'false';

export const useFeatureFlagsStore = create<FeatureFlagsState>((set) => ({
    flags: initialFlags,
    seasonalBrandingThumbnailsConstants: initialSeasonalBrandingThumbnailsConstants,
    seasonalBrandingBadgesConstants: initialSeasonalBrandingBadgesConstants,
    seasonalBrandingEnabled: initialSeasonalBrandingEnabled,
    brandingLoadStatus: {},
    setBrandingLoadStatus: (name, status) =>
        set((state) => ({ brandingLoadStatus: { ...state.brandingLoadStatus, [name]: status } })),
    setSeasonalBrandingEnabled: (enabled) => {
        localStorage.setItem(BRANDING_STORAGE_KEY, String(enabled));
        if (!enabled) {
            resetSeasonalIntroPlayed();
        }
        set({ seasonalBrandingEnabled: enabled });
    },
    setSeasonalBranding: (seasonalBranding) =>
        set((state) => ({
            flags: {
                ...state.flags,
                seasonalBranding: seasonalBranding ? (seasonalBranding.toLowerCase() as BrandingType) : null,
            },
        })),
    setFlags: (flags) =>
        set((state) => ({
            flags: {
                ...state.flags,
                ...flags,
                seasonalBranding: flags.seasonalBranding ? (flags.seasonalBranding.toLowerCase() as BrandingType) : null
            },
        })),
    resetFeatureFlags: () => set({ flags: initialFlags }),
}));

export const useSeasonalBranding = () => useFeatureFlagsStore((state) => state.seasonalBrandingEnabled ? state.flags.seasonalBranding?.toLocaleLowerCase() : undefined);

// Brandings that have NO /brandings/{branding}/group_*bell_link/panel art. An exclusion list
// rather than an allow-list on purpose: operator brands get their panel art delivered onto the
// master lines outside this repo, so their presence here can't be derived from the assets folder.
const brandingsWithoutJackpotPanelArt: string[] = ['halloween'];

// The branding to use for the branded jackpot panel image, or undefined for the default panel.
export const useSeasonalBrandingJackpotPanel = () =>
    useFeatureFlagsStore((state) => {
        const branding = state.seasonalBrandingEnabled ? state.flags.seasonalBranding?.toLocaleLowerCase() : undefined;
        return branding && !brandingsWithoutJackpotPanelArt.includes(branding) ? branding : undefined;
    });

export const useSeasonalBrandingSettings = () => useFeatureFlagsStore(useShallow((state) => ({
    hasBranding: state.flags.seasonalBranding !== null,
    enabled: state.seasonalBrandingEnabled,
    setEnabled: state.setSeasonalBrandingEnabled,
})));
export const useEnableLobbyDeposit = () => useFeatureFlagsStore((state) => state.flags.enableLobbyDeposit);
export const useEnablePrizePopCampaign = () => useFeatureFlagsStore((state) => state.flags.enablePrizePopCampaign);
export const useEnableTournamentsCampaign = () => useFeatureFlagsStore((state) => state.flags.enableTournamentsCampaign);
export const useEnableRebateCampaign = () => useFeatureFlagsStore((state) => state.flags.enableRebateCampaign);
export const useSeasonalBrandingThumbnails = () => useFeatureFlagsStore((state) => state.seasonalBrandingThumbnailsConstants);
export const useSeasonalBrandingBadges = () => useFeatureFlagsStore((state) => state.seasonalBrandingBadgesConstants);

export default useFeatureFlagsStore;
